import React, { useRef, useEffect, useState, useCallback } from "react";
import "./ColumnPicker.css";

const ColumnPicker = ({
  columnConfig,
  onToggle,
  onReorder,
  onReset,
  onClose,
  anchorRef,
}) => {
  const popupRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  // Position popup below the anchor element
  useEffect(() => {
    if (anchorRef?.current && popupRef.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const popupRect = popupRef.current.getBoundingClientRect();
      // Ensure popup doesn't overflow right edge
      let left = anchorRect.left;
      if (left + popupRect.width > window.innerWidth - 8) {
        left = window.innerWidth - popupRect.width - 8;
      }
      setPosition({
        top: anchorRect.bottom + 4,
        left,
      });
    }
  }, [anchorRef]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleDragStart = useCallback((e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback((e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIdx(idx);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIdx(null);
  }, []);

  const handleDrop = useCallback(
    (e, dropIdx) => {
      e.preventDefault();
      setDragOverIdx(null);
      if (dragIdx == null || dragIdx === dropIdx) {
        setDragIdx(null);
        return;
      }
      const keys = columnConfig.map((c) => c.key);
      const [moved] = keys.splice(dragIdx, 1);
      keys.splice(dropIdx, 0, moved);
      onReorder(keys);
      setDragIdx(null);
    },
    [dragIdx, columnConfig, onReorder],
  );

  const handleDragEnd = useCallback(() => {
    setDragIdx(null);
    setDragOverIdx(null);
  }, []);

  return (
    <div
      ref={popupRef}
      className="column-picker-popup"
      style={{ top: position.top, left: position.left }}
    >
      <div className="column-picker-title">Colunas</div>
      <div className="column-picker-list">
        {columnConfig.map((col, idx) => (
          <div
            key={col.key}
            className={`column-picker-item${dragIdx === idx ? " dragging" : ""}${dragOverIdx === idx ? " drag-over" : ""}${col.fixed ? " fixed" : ""}`}
            draggable={!col.fixed}
            onDragStart={col.fixed ? undefined : (e) => handleDragStart(e, idx)}
            onDragOver={col.fixed ? undefined : (e) => handleDragOver(e, idx)}
            onDragLeave={col.fixed ? undefined : handleDragLeave}
            onDrop={col.fixed ? undefined : (e) => handleDrop(e, idx)}
            onDragEnd={col.fixed ? undefined : handleDragEnd}
          >
            <span
              className="column-picker-drag-handle"
              style={col.fixed ? { visibility: "hidden" } : undefined}
            >
              <i className="pi pi-bars" />
            </span>
            <span className="column-picker-label">{col.label}</span>
            <input
              type="checkbox"
              className="column-picker-checkbox"
              checked={col.visible}
              onChange={(e) => onToggle(col.key, e.target.checked)}
              data-evo-id={`columnpicker-chk-${col.key}`}
            />
          </div>
        ))}
      </div>
      <div className="column-picker-reset">
        <button onClick={onReset} data-evo-id="columnpicker-btn-reset">
          Restaurar padrão
        </button>
      </div>
    </div>
  );
};

export default React.memo(ColumnPicker);
